import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin } from '../services/api';

const AdminContext = createContext();

export const useAdmin = () => {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within AdminProvider');
    }
    return context;
};

export const AdminProvider = ({ children }) => {
    const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
    const [adminData, setAdminData] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [theme, setTheme] = useState('light');
    const [sidebarActive, setSidebarActive] = useState(window.innerWidth >= 768);
    const [showLogin, setShowLogin] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Load admin state from localStorage on mount
    useEffect(() => {
        const loggedIn = localStorage.getItem('isLoggedIn');
        const userType = localStorage.getItem('userType');
        const savedTheme = localStorage.getItem('adminTheme') || 'light';

        setTheme(savedTheme);

        if (loggedIn === 'true' && userType === 'admin') {
            setIsAdminLoggedIn(true);
            setShowLogin(false);
            setAdminData({
                username: 'admin',
                role: 'admin'
            });
        } else {
            setShowLogin(true);
        }

        // Apply theme to document
        document.documentElement.classList.toggle('dark', savedTheme === 'dark');

        // Read department from URL if present
        const urlParams = new URLSearchParams(window.location.search);
        const department = urlParams.get('department');
        if (department) {
            setSelectedDepartment(decodeURIComponent(department));
        }
    }, []);

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        if (username === 'admin' && password === 'admin') {
            try {
                const response = await apiLogin({ email: 'admin@gmail.com', password: 'admin123' });
                if (response.success) {
                    setIsAdminLoggedIn(true);
                    setShowLogin(false);
                    setAdminData({
                        username: 'admin',
                        role: 'admin'
                    });
                    setLoginError('');
                    localStorage.setItem('isLoggedIn', 'true');
                    localStorage.setItem('userType', 'admin');
                    localStorage.setItem('token', response.token);
                } else {
                    setLoginError('Failed to retrieve authentication token from server.');
                }
            } catch (err) {
                console.error("Admin backend authentication failed:", err);
                setLoginError('Error connecting to backend database.');
            }
        } else {
            setLoginError('Invalid credentials');
        }
    };

    // New adminLogin function for direct login without form state
    const adminLogin = () => {
        setIsAdminLoggedIn(true);
        setShowLogin(false);
        setAdminData({
            username: 'admin',
            role: 'admin'
        });
        setLoginError('');
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userType', 'admin');
    };

    const handleLogout = () => {
        setIsAdminLoggedIn(false);
        setAdminData(null);
        setShowLogin(true);
        setUsername('');
        setPassword('');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userType');
        localStorage.removeItem('token');
        localStorage.removeItem('studentUser');
        localStorage.removeItem('selectedCourse');
    };

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        localStorage.setItem('adminTheme', newTheme);
        document.documentElement.classList.toggle('dark', newTheme === 'dark');
    };

    const value = {
        isLoggedIn: isAdminLoggedIn,
        showLogin,
        username,
        setUsername,
        password,
        setPassword,
        loginError,
        handleLogin,
        adminLogin,
        handleLogout,
        theme,
        sidebarActive,
        setSidebarActive,
        toggleTheme,
        selectedDepartment,
        setSelectedDepartment,
        searchTerm,
        setSearchTerm
    };

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};

export default AdminContext;